import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Paragraph1, Paragraph2, SmallText } from 'inpock-ui-react';
import Cookies from 'js-cookie';

import { thumbnail } from '@/utils';
import { MarketProduct as MarketProductType } from '@/apis';
import { Image } from '@/components/common';
import { useBeaconRequest } from '@/hooks';

import * as S from './styled';

function ProductPrice({
  price,
  original_price,
  discount_rate,
  isEnd,
}: {
  price: number;
  original_price: number;
  discount_rate: number;
  isEnd?: boolean;
}) {
  if (isEnd) {
    return (
      <S.ProductPrice>
        <Paragraph1 className="text-gray-400" style={{ fontWeight: '400' }}>
          {original_price.toLocaleString()}원
        </Paragraph1>
      </S.ProductPrice>
    );
  }

  return (
    <S.ProductPrice>
      <div className="price-wrapper">
        <Paragraph2 className="text-orange-900" style={{ fontWeight: '500' }}>
          {discount_rate}%
        </Paragraph2>
        <Paragraph1>{price.toLocaleString()}원</Paragraph1>
      </div>
      <SmallText className="original-price text-gray-400">
        {original_price.toLocaleString()}원
      </SmallText>
    </S.ProductPrice>
  );
}

function ProductReview({ review }: { review: { avr_rating: number; counts: number } }) {
  return (
    <S.ProductReview>
      <div className="icon-star" />
      <SmallText className="text-gray-500">
        {review.avr_rating} ({review.counts.toLocaleString()})
      </SmallText>
    </S.ProductReview>
  );
}

export function MarketProduct({
  campaign,
  marketId,
  version,
}: {
  campaign: MarketProductType;
  marketId: number;
  version?: string;
}) {
  const navigate = useNavigate();
  const { beaconRequest } = useBeaconRequest();
  const [remainingTime, setRemainingTime] = useState('');
  const 진행예정 = new Date(campaign.open_at) > new Date();
  const 진행중 =
    new Date(campaign.open_at) < new Date() && new Date(campaign.open_until) > new Date();
  const 판매종료 = !campaign.is_permanent && new Date(campaign.open_until) < new Date();

  const calculateTimeRemaining = (until: string) => {
    const now = new Date().getTime();
    const remainingTimeMs = new Date(until).getTime() - now;
    const day = Math.floor(remainingTimeMs / (1000 * 60 * 60 * 24));
    const hour = Math.floor((remainingTimeMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minute = Math.floor((remainingTimeMs % (1000 * 60 * 60)) / (1000 * 60));
    const second = Math.floor((remainingTimeMs % (1000 * 60)) / 1000);

    return {
      day,
      hour,
      minute,
      second,
    };
  };

  const getOpenDate = () => {
    const month =
      new Date(campaign.open_at).getMonth() < 9
        ? `0${new Date(campaign.open_at).getMonth() + 1}`
        : new Date(campaign.open_at).getMonth() + 1;
    const day =
      new Date(campaign.open_at).getDate() < 10
        ? `0${new Date(campaign.open_at).getDate()}`
        : new Date(campaign.open_at).getDate();

    return `${month}월 ${day}일 오픈`;
  };

  const onClickMarketCampaign = () => {
    const { REACT_APP_EVENT_ENDPOINT: endpoint = '' } = process.env;
    const params = {
      event_name: '@market/click_campaign',
      event_params: {
        _ga: Cookies.get('_ga'),
        market_id: marketId,
        campaign_id: campaign.id,
        user_agent: navigator.userAgent,
      },
    };

    beaconRequest(endpoint, params);
    const url = version ? `/${version}/products/${campaign.id}` : `/products/${campaign.id}`;
    navigate(url);
  };

  useEffect(() => {
    const updateRemainingTime = () => {
      if (!campaign.open_at || !campaign.open_until) return;
      const { day, hour, minute, second } = calculateTimeRemaining(
        진행예정 ? campaign.open_at : campaign.open_until,
      );

      setRemainingTime(
        `${day}일 ${hour}시간 ${minute}분 ${second}초 ${진행예정 ? '뒤 오픈' : '남음'}`,
      );
    };

    updateRemainingTime();
    const interval = setInterval(updateRemainingTime, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [campaign]);

  return (
    <S.MarketProductWrapper onClick={onClickMarketCampaign}>
      <S.MarketProductContainer>
        <Image
          lazy
          src={thumbnail.origin(campaign.images[0].image)}
          borderRadius={8}
          style={{ position: 'absolute' }}
          alt={campaign.name}
          threshold={0.1}
        />
        {진행예정 && (
          <S.OpenDate>
            <Paragraph2 className="text-white text-bold" style={{ letterSpacing: '-0.42px' }}>
              {getOpenDate()}
            </Paragraph2>
          </S.OpenDate>
        )}
      </S.MarketProductContainer>
      <S.ProductTitle className="text-gray-700">{campaign.name}</S.ProductTitle>
      <ProductPrice
        price={campaign.price}
        original_price={campaign.original_price}
        discount_rate={campaign.discount_rate}
        isEnd={판매종료}
      />
      {(진행예정 || 진행중) && (
        <SmallText
          className={진행예정 ? 'text-gray-500' : 'text-gray-700'}
          style={{ letterSpacing: '-0.36px' }}
        >
          {remainingTime}
        </SmallText>
      )}
      <ProductReview review={campaign.review_data} />
      {판매종료 && (
        <S.EndBadge>
          <SmallText className="text-white">SOLD OUT</SmallText>
        </S.EndBadge>
      )}
    </S.MarketProductWrapper>
  );
}
